import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import updateLocale from "dayjs/plugin/updateLocale";

dayjs.extend(relativeTime);
dayjs.extend(updateLocale);

type PurchaserDateFormat = "MMM D, YYYY" | "MMM D, YYYY h:mm A" | "MMMM D, YYYY" | "MMM D" | "hh:mm" | "MM / DD / YYYY" | "M/D/YY";
type PurchaserDateCustomOption = "ShortDynamic" | "Relative" | "SemiRelative";

export const DAYS_OF_WEEK = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
export const FULL_MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

/**
 * Dates that really mean "blank". An empty cell in a quote document becomes the number zero
 * somewhere in extraction, and zero formats as a date: 1899-11-30 is day zero in Excel,
 * 1900-01-01 is day zero in older spreadsheets, and 1970-01-01 is timestamp zero in Unix.
 * All three parse as perfectly valid dates, so they reach the screen looking real, as in
 * "Valid Jan 1, 1970", unless a caller rejects them.
 */
const ZERO_DATES = new Set(["1899-11-30", "1900-01-01", "1970-01-01"]);

const DateUtil = {
    getValidDate(input?: any): Date | undefined {
        // 2000000000 === 1/23/1970 in epoch milliseconds, this likely means it's an epoch timestamp
        // but in seconds so just multiply by 1000
        if (typeof input === "number" && input < 2000000000) {
            input *= 1000;
        }

        const d = dayjs(input);
        if (input === undefined || !d.isValid()) return undefined;
        return d.toDate();
    },
    isZeroDate(input?: string | number | Date | null): boolean {
        const date = this.getValidDate(input ?? undefined);
        if (!date) {
            return false;
        }

        // Compare the day in UTC.
        const year = date.getUTCFullYear();
        const month = String(date.getUTCMonth() + 1).padStart(2, "0");
        const day = String(date.getUTCDate()).padStart(2, "0");
        return ZERO_DATES.has(`${year}-${month}-${day}`);
    },
    /** The start of the current day, week, month, or year in the viewer's timezone, as a timestamp. Weeks start on Sunday. */
    startOfCurrent(unit: "day" | "week" | "month" | "year"): number {
        const now = new Date();
        const [year, month, day] = [now.getFullYear(), now.getMonth(), now.getDate()];
        switch (unit) {
            case "day":
                return new Date(year, month, day).getTime();
            case "week":
                // A negative day of the month rolls back into the previous month.
                return new Date(year, month, day - now.getDay()).getTime();
            case "month":
                return new Date(year, month, 1).getTime();
            case "year":
                return new Date(year, 0, 1).getTime();
        }
    },
    /** The last millisecond of the day a timestamp falls on, in the viewer's timezone. */
    endOfDay(timestamp: number): number {
        const date = new Date(timestamp);
        return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999).getTime();
    },
    isSameDay(input?: any): boolean {
        const now = dayjs();
        const inputDate = dayjs(this.getValidDate(input));
        return inputDate.isSame(now, "day");
    },
    formatted(input?: any, options?: { format?: PurchaserDateFormat; custom?: PurchaserDateCustomOption; includeTimezone?: boolean }): string {
        if (!input || input === null) return "";

        const date = this.getValidDate(input);

        if (!date) {
            console.error(`Failed to parse date from input: ${input}`);
            return "";
        }

        // Default to the most used date format we have
        const defaultFormat: PurchaserDateFormat = "MMM D, YYYY";

        if (!options) {
            return dayjs(date).format(defaultFormat);
        }

        // Gmail inspired
        // Same day -- 2:30PM
        // Same year -- Jul 23
        // Different year -- Dec 31, 2024
        if (options.custom === "ShortDynamic") {
            const now = dayjs();
            const inputDate = dayjs(date);

            if (inputDate.isSame(now, "day")) {
                return inputDate.format("h:mm A");
            }
            if (inputDate.isSame(now, "year")) {
                return inputDate.format("MMM D");
            }

            return inputDate.format("MMM D, YYYY");
        }

        if (options.custom === "Relative") {
            return dayjs(date).fromNow();
        }

        if (options.custom === "SemiRelative") {
            const now = dayjs();
            const inputDate = dayjs(date);
            const secondsDiff = now.unix() - inputDate.unix();

            if (secondsDiff >= 0) {
                if (secondsDiff < 60) {
                    return "Just now";
                } else if (secondsDiff < 120) {
                    return "A few seconds ago";
                } else if (secondsDiff < 3600) {
                    const minutes = Math.floor(secondsDiff / 60);
                    return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
                }
            } else {
                const absSecondsDiff = Math.abs(secondsDiff);
                if (absSecondsDiff < 60) {
                    return "In a few seconds";
                } else if (absSecondsDiff < 120) {
                    return "In a few minutes";
                } else if (absSecondsDiff < 3600) {
                    const minutes = Math.floor(absSecondsDiff / 60);
                    return `In ${minutes} minute${minutes > 1 ? "s" : ""}`;
                }
            }

            // For some reason dayjs doesn't like getting the short version of the timezone so we'll use plain Date
            // outputs in the format: 7/24/2025, 2:33:11 PM EDT
            const dateStr = inputDate.toDate().toLocaleString("en-US", { timeZoneName: "short" });
            // so extract the EDT
            const TZ = dateStr.split(" ").pop() || "";

            return `${inputDate.format("MMMM D, YYYY h:mm A")} ${TZ}`;
        }

        if (options.includeTimezone) {
            const dateStr = dayjs(date).toDate().toLocaleString("en-US", { timeZoneName: "short" });
            const TZ = dateStr.split(" ").pop() || "";
            const format = options.format ?? defaultFormat;
            return `${dayjs(date).format(format)} ${TZ}`;
        }

        const format = options.format ?? defaultFormat;
        return dayjs(date).format(format);
    },
    /**
     * FIXME: Hacky fix to drop UTC and just display the raw day value. A better solution would handle timezones,
     * but we don't have a ton of support for that right now, so we just want to treat it as the raw value.
     * This will basically just assume the date is for the current user's timezone, and since we generally
     * only care about the date (and not time) it should be fine for now.
     */
    stripUtcTimezone(strDate: string) {
        return strDate.replace("Z", "");
    },
};

// --- From here down is just what is required to setup support for relative date ---

dayjs.updateLocale("en", {
    relativeTime: {
        future: "in %s",
        past: "%s ago",
        s: "a few seconds", // Overridden
        m: "a minute",
        mm: "%d minutes",
        h: "an hour",
        hh: "%d hours",
        d: "a day", // Overridden to today or yesterday below
        dd: "%d days", // 7-25 overridden to "a week" -> "# weeks"
        M: "a month",
        MM: "%d months",
        y: "a year",
        yy: "%d years",
    },
});

// Store the original fromNow function before we override it
const originalFromNow = dayjs.prototype.fromNow;

dayjs.prototype.fromNow = function (withoutSuffix?: boolean): string {
    const now = dayjs();

    const diffDays = now.diff(this, "day");
    if (now.isAfter(this) && diffDays === 1) return "yesterday";
    if (now.isBefore(this) && diffDays === -1) return "tomorrow";

    const diffSeconds = now.diff(this, "second");
    if (Math.abs(diffSeconds) < 60) {
        return "just now"; // within 60 seconds return "just now"
    }

    // Intercept anything from 7 to 25 days, dayjs default is 26 for a month
    const absDiffDays = Math.abs(diffDays);

    if (absDiffDays >= 7 && absDiffDays <= 25) {
        // Calculate the number of weeks, rounded to the nearest week
        const weeks = Math.round(absDiffDays / 7);
        let result: string;

        if (weeks <= 1) {
            result = "a week";
        } else {
            result = `${weeks} weeks`;
        }

        // Can go ahead and return without the suffix if specified
        if (withoutSuffix) {
            return result;
        }

        const isPast = now.isAfter(this);
        return isPast ? `${result} ago` : `in ${result}`;
    }

    // Call the original as a fallback
    return originalFromNow.call(this, withoutSuffix);
};

export default DateUtil;

